import { useCallback, useState } from 'react';
import { StrapiFile } from 'types/StrapiFile';
import { Form } from 'react-final-form';
import DocumentItem from './DocumentItem';
import FormControl from 'common/components/FormControl/FormControl';
import { useTranslation } from 'react-i18next';

interface DocumentSectionProps {
  documents: (StrapiFile & { authorType?: 'applicant' | 'mediator' })[];
  meetingId?: number;
  applicantName?: string;
  onDocumentAdded?: (documentId: number) => Promise<void>;
  onDocumentDeleted?: (documentId: number) => Promise<void>;
  canEdit?: boolean;
}

/**
 * Section pour afficher et gérer les documents
 */
const DocumentSection = ({
  documents,
  meetingId,
  applicantName,
  onDocumentAdded,
  onDocumentDeleted,
  canEdit = false,
}: DocumentSectionProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fonction pour supprimer un document
  const handleDeleteDocument = useCallback(
    async (documentId: number) => {
      if (!onDocumentDeleted) return;
      await onDocumentDeleted(documentId);
    },
    [onDocumentDeleted]
  );

  // Fonction pour ajouter un document
  const handleAddDocument = useCallback(
    async (document: any) => {
      if (!document || !onDocumentAdded) {
        return;
      }

      try {
        setIsSubmitting(true);
        // Appeler le callback pour ajouter les documents
        await onDocumentAdded(document.id);
      } catch (error) {
        console.error("Erreur lors de l'ajout du document:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onDocumentAdded]
  );

  return (
    <div className="mt-4">
      <div className="flex flex-row justify-start items-center">
        {documents.length ? (
          <h3 className="text-md font-medium">
            {documents.length === 1
              ? '1 document partagé'
              : `${documents.length} documents partagés`}
          </h3>
        ) : (
          <p className="text-sm font-medium text-gray-400 flex-1 text-center italic">
            Aucun document partagé
          </p>
        )}
        {isSubmitting && (
          <div className="ml-2">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite]"></span>
          </div>
        )}
      </div>

      {/* Liste des documents */}
      <div className="mt-2">
        {documents.map((document) => (
          <DocumentItem
            key={document.id}
            document={document}
            applicantName={applicantName}
            onDelete={handleDeleteDocument}
            canDelete={canEdit}
          />
        ))}
      </div>

      {canEdit && (
        <div className="mt-4">
          <Form
            onSubmit={(values) => handleAddDocument(values.documents[0])}
            render={({ form, handleSubmit }) => (
              <div className="w-full flex flex-row items-center justify-end">
                <FormControl
                  name="documents"
                  type="file"
                  onChange={() => {
                    // Soumettre automatiquement le formulaire lorsqu'un fichier est sélectionné
                    setTimeout(() => {
                      handleSubmit();
                      form.reset();
                    }, 0);
                  }}
                  autoSubmit
                />
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentSection;
